import React, { useState, useEffect } from "react";
import {
  authAPI,
  signupAPI,
  devicesAPI,
  shopsAPI,
  shopSession,
  deviceFingerprint,
} from "../services/api";
import { useI18n } from "../i18n";
import LanguageSelect from "../components/LanguageSelect";
import { formatDate } from "../utils/dates";

/**
 * What a fresh install shows before it is allowed to do anything.
 *
 * Activation deliberately needs someone who works in the shop to sign in: a
 * device is a licence, and handing one out to whoever opens the URL would make
 * the limit meaningless. Once activated the token is stored and this screen is
 * never seen again on this device.
 */
const suggestedLabelKey = () => {
  const ua = navigator.userAgent;

  if (/iPad|Tablet/i.test(ua)) return "tablet";
  if (/iPhone|Android|Mobile/i.test(ua)) return "phone";
  return "laptop";
};

export default function ActivateDevice({ onActivated }) {
  const { t } = useI18n();
  const [step, setStep] = useState("signin");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [label, setLabel] = useState(() =>
    t(`auth.activate.labels.${suggestedLabelKey()}`),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [blockedBy, setBlockedBy] = useState(null);
  const [signupOpen, setSignupOpen] = useState(false);
  // Someone who works in several shops says which one this device is for
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState(null);

  // A stranger's first screen is this one, so it is where "create a shop" lives
  useEffect(() => {
    signupAPI
      .config()
      .then(({ data }) => setSignupOpen(Boolean(data.enabled)))
      .catch(() => {});
  }, []);

  /** After signing in: one shop goes straight on, more than one asks which. */
  const afterSignIn = async () => {
    try {
      const { data } = await shopsAPI.mine();
      const mine = Array.isArray(data) ? data : [];
      setShops(mine);
      if (mine.length > 1) {
        setStep("pick");
        return;
      }
    } catch {
      // The activation itself will say what is wrong
    }
    setStep("name");
  };

  useEffect(() => {
    // Already signed in as a shop member? Skip straight to naming the device.
    if (localStorage.getItem("token")) afterSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, []);

  const signIn = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const { data } = await authAPI.login(identifier, password);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));
      await afterSignIn();
    } catch (err) {
      setError(err.response?.data?.error || t("auth.activate.notRecognised"));
    } finally {
      setBusy(false);
    }
  };

  const activate = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setBlockedBy(null);

    try {
      const { data } = await devicesAPI.activate(
        {
          label,
          platform: navigator.userAgent,
          fingerprint: deviceFingerprint(),
        },
        shopId || undefined,
      );

      // Kept per shop, with the name, so opening another of the user's shops
      // later activates this device there without asking again
      shopSession.setLabel(label);
      shopSession.use(data.shop.id, data.device_token);
      onActivated?.(data);
    } catch (err) {
      const details = err.response?.data?.details;

      if (details?.devices) {
        // Name the devices holding the slots so the owner can free one
        setBlockedBy(details);
      }

      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          t("auth.activate.failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  const field =
    "mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-base focus:border-primary-500 focus:ring-1 focus:ring-primary-500";

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
        <div className="flex items-start justify-between gap-3">
          <h1 className="text-xl font-semibold text-gray-900">
            {t("auth.activate.title")}
          </h1>
          <LanguageSelect />
        </div>
        <p className="mt-2 text-sm text-gray-600">
          {step === "signin"
            ? t("auth.activate.signinIntro")
            : step === "pick"
              ? t("shops.pick.intro")
              : t("auth.activate.nameIntro")}
        </p>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {blockedBy && (
          <div className="mt-4 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
            <p className="text-sm font-medium text-amber-900">
              {t("auth.activate.allInUse", { limit: blockedBy.device_limit })}
            </p>
            <ul className="mt-2 space-y-1 text-sm text-amber-800">
              {blockedBy.devices.map((device) => (
                <li key={device.id} className="flex justify-between gap-3">
                  <span>{device.label}</span>
                  <span className="text-amber-700 shrink-0">
                    {device.last_seen ? formatDate(device.last_seen) : ""}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 text-sm text-amber-800">
              {t("auth.activate.releaseHint")}
            </p>
          </div>
        )}

        {step === "pick" ? (
          <div className="mt-6">
            <h2 className="text-base font-semibold text-gray-900">{t("shops.pick.title")}</h2>
            <ul className="mt-3 space-y-2">
              {shops.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setShopId(entry.id);
                      setStep("name");
                    }}
                    className="w-full flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-4 py-3 text-start hover:border-primary-400 hover:bg-gray-50"
                  >
                    <span className="min-w-0">
                      <span className="block font-semibold text-gray-900 truncate">{entry.name}</span>
                      <span className="block text-xs text-gray-500" dir="ltr">/{entry.slug}</span>
                    </span>
                    {entry.is_active === false && (
                      <span className="shrink-0 text-xs font-semibold text-red-600">{t("shops.pick.suspended")}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : step === "signin" ? (
          <form onSubmit={signIn} className="mt-6 space-y-4">
            <div>
              <label htmlFor="activate-1" className="block text-sm font-medium text-gray-700">
                {t("auth.login.identifier")}
              </label>
              <input id="activate-1"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                autoComplete="username"
                required
                className={field}
              />
            </div>
            <div>
              <label htmlFor="activate-2" className="block text-sm font-medium text-gray-700">
                {t("auth.login.password")}
              </label>
              <input id="activate-2"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                className={field}
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-white font-medium disabled:opacity-60"
            >
              {busy ? t("auth.activate.signingIn") : t("auth.activate.continue")}
            </button>
            {/* This screen sits outside the router, so a plain link */}
            <p className="text-center text-sm">
              <a href="/forgot-password" className="text-primary-600 hover:underline">
                {t("auth.forgot.link")}
              </a>
            </p>
            {signupOpen && (
              <p className="text-center text-sm">
                <a href="/signup" className="font-semibold text-primary-600 hover:underline">
                  {t("signup.link")}
                </a>
              </p>
            )}
          </form>
        ) : (
          <form onSubmit={activate} className="mt-6 space-y-4">
            <div>
              <label htmlFor="activate-3" className="block text-sm font-medium text-gray-700">
                {t("auth.activate.deviceName")}
              </label>
              <input id="activate-3"
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                required
                minLength={2}
                className={field}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("auth.activate.deviceNameHint")}
              </p>
            </div>
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-white font-medium disabled:opacity-60"
            >
              {busy
                ? t("auth.activate.activating")
                : t("auth.activate.activate")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
